import { Chip } from "@mui/material";
import { FC } from "react";
import useEvent from "react-use-event-hook";

import {
  useDrawers,
  useHasPermissionsCheck,
  type RydeUiUnit,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import {
  PROCESS_WARNING_ACCENT,
  ProcessIdsWrapStyled,
} from "../process.styled";

const PROCESS_CHIP_TONE_COLORS = {
  success: rydeColors.green,
  warning: PROCESS_WARNING_ACCENT,
  skipped: rydeColors.orange,
} as const;

export const ProcessEntityIdChips: FC<{
  entity: RydeUiUnit;
  ids: number[];
  tone: keyof typeof PROCESS_CHIP_TONE_COLORS;
}> = ({ entity, ids, tone }) => {
  const {
    openEditFormDrawer,
    openRideEditFormDrawer,
    openBookingEditFormDrawer,
    openJourneyEditFormDrawer,
    openCourseEditFormDrawer,
    openStationRouteEditFormDrawer,
  } = useDrawers();
  const { hasPermissionsCheck } = useHasPermissionsCheck();
  const canOpenEntityDrawer = hasPermissionsCheck({
    entity,
    action: "read",
  });
  const accent = PROCESS_CHIP_TONE_COLORS[tone];

  const onEntityChipClick = useEvent((id: number) => {
    if (!canOpenEntityDrawer) {
      return;
    }

    switch (entity) {
      case "ride":
        void openRideEditFormDrawer({ id });
        return;
      case "booking":
        void openBookingEditFormDrawer({ id });
        return;
      case "journey":
        void openJourneyEditFormDrawer({ id });
        return;
      case "course":
        void openCourseEditFormDrawer({ id });
        return;
      case "stationRoute":
        void openStationRouteEditFormDrawer({ id });
        return;
      default:
        void openEditFormDrawer({ id, entity });
    }
  });

  if (!ids.length) {
    return null;
  }

  return (
    <ProcessIdsWrapStyled>
      {ids.map(id => (
        <Chip
          key={id}
          size="small"
          variant="outlined"
          clickable={canOpenEntityDrawer}
          component={canOpenEntityDrawer ? "button" : "div"}
          label={`#${id}`}
          onClick={() => onEntityChipClick(id)}
          sx={{
            height: 24,
            fontWeight: 600,
            color: accent,
            borderColor: accent,
            backgroundColor: rydeColors.white,
            cursor: canOpenEntityDrawer ? "pointer" : "default",
            textDecoration: canOpenEntityDrawer ? "underline" : "none",
            textUnderlineOffset: "2px",
            "&:hover": canOpenEntityDrawer
              ? {
                  backgroundColor: rydeColors.smoke,
                  borderColor: accent,
                }
              : undefined,
          }}
        />
      ))}
    </ProcessIdsWrapStyled>
  );
};
